<ng-template #reportViewModal let-modal>
  <div class="modal-header d-flex align-items-center justify-content-between no-print">
    <h4 class="modal-title mb-0">Analytical Report View</h4>
    <div class="d-flex gap-2 align-items-center">
      @if (availableLanguages.length > 0) {
        <div style="width: 200px;">
          <ng-select [items]="availableLanguages"
            bindLabel="code"
            [clearable]="false"
            [(ngModel)]="currentLang"
            (change)="onLanguageChange($event)"
            placeholder="Language">
            <ng-template ng-option-tmp let-item="item">
              <span>{{ item.code }}</span>
              @if (item.status === 'generating') {
                <span class="text-warning ms-2 small"> (Generating) {{ (item.progress || 0) | number:'1.0-2' }}%</span>
              }
              @if (item.status === 'failed') {
                <span class="text-danger ms-2 small"> (Failed)</span>
              }
            </ng-template>
          </ng-select>
        </div>
      }

      <button class="btn btn-outline-info" (click)="onAddLanguage()" [ngbTooltip]="'Generate Report in different language'">
        <i class="ri-add-line align-middle"></i>
      </button>

      <div class="d-inline-block" [ngbTooltip]="'Print Report'">
        <button class="btn btn-outline-warning" (click)="printReport()" [disabled]="isLoading || errorMessage || statusMessage">
          <i class="ri-printer-line align-middle"></i>
        </button>
      </div>

      <div class="d-inline-block" [ngbTooltip]="'Refresh Reports'">
        <button class="btn btn-outline-success refresh-button" (click)="refreshReports()" [disabled]="refreshButtonDisabled" [ngClass]="{'refreshing': isRefreshing}">
          <span class="spinner-icon align-middle"> <i class="ri-refresh-line align-middle"></i> </span>
        </button>
      </div>
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.close()"></button>
    </div>
  </div>
  <div class="modal-body report-modal-body" [ngClass]="{'rtl-mode': isRtl, 'ltr-mode': !isRtl}">
    @if (isLoading) {
      <div class="text-center py-5 no-print">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Fetching Report Data...</p>
      </div>
    }
    @if (errorMessage) {
      <div class="alert alert-danger m-3 no-print">
        <i class="bx bx-error me-2"></i> {{ errorMessage }}
      </div>
    }
    @if (statusMessage && !isLoading) {
      <div class="text-center py-5 no-print">
        <div class="alert alert-info d-inline-block">
          <p>{{ statusMessage }}</p>
        </div>
      </div>
    }
    @if (!isLoading && !errorMessage && !statusMessage) {
      <div class="report-container" [innerHTML]="renderedContent"></div>
    }
  </div>
</ng-template>

<ng-template #onAddLanguageModal let-modal>
  <div class="modal-content">
    <div class="modal-header p-3">
      <h4 class="card-title mb-0">Add New Report Language</h4>
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body" [ngClass]="canAddLanguage ? '' : 'text-center p-5'">
      @if (!canAddLanguage) {
        <div class="mt-2">
          <lord-icon src="https://cdn.lordicon.com/tdrtiskw.json" trigger="loop" colors="primary:#f06548,secondary:#f7b84b" style="width:120px;height:120px"></lord-icon>
          <h4 class="mb-3 mt-4">Initial Report generation in progress</h4>
          <p class="text-muted mx-4 mb-4">
            Please wait for the initial report to complete before adding new languages.
          </p>
        </div>
      }
      @if (canAddLanguage) {
        <form [formGroup]="addLanguageForm" (ngSubmit)="onAddLanguageSubmit()">
          <div class="mb-4">
            <label class="form-label">Select Language <span class="text-danger">*</span></label>
            <ng-select [items]="filteredLanguageList"
              bindLabel="name"
              placeholder="Choose a language..."
              formControlName="language"
              [clearable]="false"
              [searchable]="true"
              [ngClass]="{ 'is-invalid': submitted && addLanguageForm.get('language')?.errors }">
              <ng-template ng-option-tmp let-item="item">
                <span class="badge bg-info me-2">{{item.prefix}}</span> {{item.name}}
              </ng-template>
            </ng-select>
            @if (submitted && addLanguageForm.get('language')?.errors) {
              <div class="invalid-feedback d-block">
                Please select a language.
              </div>
            }
            @if (filteredLanguageList.length === 0) {
              <div class="text-warning small mt-2">
                <i class="ri-alert-line"></i> All available languages have already been generated.
              </div>
            }
          </div>
          <div class="text-end">
            <button type="button" class="btn btn-light me-2" (click)="modal.dismiss()">Cancel</button>
            <button type="submit" class="btn btn-primary btn-load"
              [disabled]="spinner || filteredLanguageList.length === 0">
              <span class="d-flex justify-content-center align-items-center">
                @if (spinner) {
                  <span class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </span>
                }
                <span [ngClass]="spinner ? 'ms-2' : ''">Generate</span>
              </span>
            </button>
          </div>
        </form>
      }
    </div>
  </div>
</ng-template>

<ng-template #responseModal let-modal>
  <div class="modal-content">
    <div class="modal-body text-center p-5">
      <div class="text-end">
        <button class="btn-close text-end" data-bs-dismiss="modal" aria-label="Close" (click)="closeModal()"></button>
      </div>
      <div class="mt-2">
        @if (success) {
          <lord-icon src="https://cdn.lordicon.com/tqywkdcz.json" trigger="hover" style="width:150px;height:150px"></lord-icon>
        }
        @if (!success) {
          <lord-icon src="https://cdn.lordicon.com/tdrtiskw.json" trigger="loop" colors="primary:#f06548,secondary:#f7b84b" style="width:120px;height:120px"></lord-icon>
        }
        <h4 class="mb-3 mt-4">{{ message }}</h4>
        <p class="text-muted mx-4 mb-0">{{ message }}</p>
      </div>
    </div>
  </div>
</ng-template>